import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  id?: string;
  'aria-labelledby'?: string;
}

/**
 * Liste déroulante entièrement stylée (thème Aglomera) — remplace le &lt;select&gt; natif
 * dont le menu ouvert ne peut pas être personnalisé dans les navigateurs.
 */
const SelectDropdown: React.FC<SelectDropdownProps> = ({
  value,
  onChange,
  options,
  id,
  'aria-labelledby': ariaLabelledby,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = options.find(o => o.value === value);
  const label = selected?.label ?? options[0]?.label ?? '';

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        type="button"
        id={id}
        aria-labelledby={ariaLabelledby}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
        className="flex w-full items-center justify-between gap-3 rounded-xl border bg-white px-4 py-2.5 text-left text-sm font-medium text-gray-800 shadow-sm transition-all hover:border-[#93b4f0] focus:outline-none focus:border-[#2a52a8] focus:ring-[3px] focus:ring-[rgba(42,82,168,0.14)]"
        style={{ borderColor: open ? '#2a52a8' : '#c7d7f5' }}
      >
        <span className="min-w-0 truncate">{label}</span>
        <ChevronDown
          size={18}
          className="flex-shrink-0 text-[#2a52a8] transition-transform duration-200"
          style={{ transform: open ? 'rotate(180deg)' : undefined }}
          aria-hidden
        />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute z-50 mt-1.5 max-h-60 w-full overflow-auto rounded-xl border bg-white py-1 shadow-lg ring-1 ring-black/5"
          style={{ borderColor: '#c7d7f5' }}
        >
          {options.map(opt => {
            const isSel = opt.value === value;
            return (
              <li key={opt.value || '__empty'} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={isSel}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center px-4 py-2.5 text-left text-sm transition-colors ${
                    isSel
                      ? 'bg-[#e6edfa] font-semibold text-[#2a52a8] hover:bg-[#dce6fb]'
                      : 'bg-white font-medium text-gray-700 hover:bg-[#f0f4ff] hover:text-gray-900'
                  }`}
                >
                  {opt.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SelectDropdown;
